import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useForm
  , ErrorMessage } from 'react-hook-form';

import Form from 'deltoid/core/Form';
import { Row, Col } from 'deltoid/core/Blocks/BootstrapContainers';
import { Body, Small } from 'deltoid/core/Blocks/Typography';
import RadioButton from '@components/BaseComponents/RadioButton';
import FormFooter from '@components/BaseComponents/FormFooter';

import { kycActions, kycSelectors, merchantSelectors } from '@store/ducks';
import { pincodeRegex, addressRegex } from '@utils/validations';

const Address = ({
  getRegistrationAddress,
  getOperatingAddress,
  getSavedKycAddress,
  submitAddress,
  processing,
  fetchPincode,
  getPincodeDetails,
  editable,
  why,
  isManual,
  getMerchantBusinessDetails,
}) => {
  const { register, handleSubmit, errors, getValues, watch, reset, setValue, } = useForm(
    { defaultValues: { sameAsBusinessAddress: 'false' } },
  );
  useEffect(() => {}, [watch('sameAsBusinessAddress')]);

  useEffect(() => {
    const addressDetails = {}
    if (getRegistrationAddress) {
      addressDetails.registration_address = getRegistrationAddress;
      addressDetails.operating_address = getOperatingAddress;
    } else {
      addressDetails.registration_address = getSavedKycAddress ? getSavedKycAddress : {};
    }
    reset({
      ...addressDetails,
      company_name: getMerchantBusinessDetails?.business_name || getMerchantBusinessDetails?.pancard_name,
      sameAsBusinessAddress: getRegistrationAddress ? (
        (
          addressDetails?.operating_address?.address_line === addressDetails?.registration_address?.address_line
          && addressDetails?.operating_address?.pincode === addressDetails?.registration_address?.pincode
        ) ? 'true' : 'false'
      ) : 'true',
    });
  }, [getRegistrationAddress, getOperatingAddress, getSavedKycAddress]);

  const onSubmit = (formData) => {
    submitAddress({formData, isManual})
  };

  useEffect(() => {
    if (getPincodeDetails && getPincodeDetails.registration_address)
      setValue([
        { 'registration_address.state': getPincodeDetails.registration_address.state },
        { 'registration_address.city': getPincodeDetails.registration_address.city },
      ])
    if (getPincodeDetails && getPincodeDetails.operating_address)
      setValue([
        { 'operating_address.state': getPincodeDetails.operating_address.state },
        { 'operating_address.city': getPincodeDetails.operating_address.city },
      ])
  }, [getPincodeDetails]);

  const renderAddressForm = (type) => (
    <Row  className='mb-3 px-4'>
      <Col md={6}>
        <Form.Group>
          <Form.Label id={`${type}.address_line`}>Address</Form.Label>
          <Form.Text
            ref={register({
              required: 'This field can not be empty',
              validate: { regex: addressRegex },
            })}
            id={`${type}.address_line`}
            name={`${type}.address_line`}
            readOnly={!editable}
          />
          <ErrorMessage errors={errors} name={`${type}.address_line`}>
            {({ message }) => (
              <Form.Message type='error'>
                {message}
              </Form.Message>
            )}
          </ErrorMessage>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group>
          <Form.Label id={`${type}.pincode`}>Postal Code</Form.Label>
          <Form.Text
            ref={register({
              required: 'This field can not be empty',
              validate: { regex: pincodeRegex }
            })}
            id={`${type}.pincode`}
            name={`${type}.pincode`}
            readOnly={!editable}
            onChange={(e) => {
              fetchPincode(type, pincodeRegex(e.target.value) ? false : e.target.value);
            }}
          />
          <ErrorMessage errors={errors} name={`${type}.pincode`}>
            {({ message }) => (
              <Form.Message type='error'>
                {message}
              </Form.Message>
            )}
          </ErrorMessage>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group>
          <Form.Label id={`${type}.city`}>City</Form.Label>
          <Form.Text
            ref={register}
            id={`${type}.city`}
            name={`${type}.city`}
            readOnly={true}
            placeholder='Would be filled based on pincode'
          />
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group>
          <Form.Label id={`${type}.state`}>State</Form.Label>
          <Form.Text
            ref={register}
            id={`${type}.state`}
            name={`${type}.state`}
            readOnly={true}
            placeholder='Would be filled based on pincode'
          />
        </Form.Group>
      </Col>
    </Row>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid='address-form'>
      <Row className='px-4'>
        <Col md={6}>
          <Form.Group>
            <Form.Label id='company_name'>Name</Form.Label>
            <Form.Text
              ref={register({
                required: 'This field can not be empty',
              })}
              id='company_name'
              name='company_name'
              readOnly
            />
          </Form.Group>
        </Col>
      </Row>
      {renderAddressForm('registration_address')}
      <Row className='px-4' noGutters>
        <Col className='pb-3'>
          <Small variant='medium' className='pb-2'>Do you have a different Operating Business Address ?</Small>
          <RadioButton
            className='pr-3'
            ref={register}
            name='sameAsBusinessAddress'
            value={false}
            label='Yes'
            disabled={!editable}
          />
          <RadioButton
            ref={register}
            name='sameAsBusinessAddress'
            value={true}
            label='No, same as above'
            disabled={!editable}
          />
        </Col>
      </Row>
      {getValues('sameAsBusinessAddress') === 'false' && renderAddressForm('operating_address')}
      {
        editable && (
          <FormFooter
            tooltipProps={why}
            btnProps={{
              label: 'Confirm and Proceed',
              type: "button",
              onClick: handleSubmit(onSubmit),
              disabled: processing,
              loading: processing,
            }}
          />
      )}
    </form>
  )
};

const mapStateToProps = (state) => ({
  getRegistrationAddress: merchantSelectors.getRegistrationAddress(state),
  getOperatingAddress: merchantSelectors.getOperatingAddress(state),
  getSavedKycAddress: merchantSelectors.getSavedKycAddress(state),
  processing: kycSelectors.submitAddressProcessing(state),
  getPincodeDetails: kycSelectors.getPincodeDetails(state),
  getMerchantBusinessDetails: merchantSelectors.getMerchantBusinessDetails(state),
});

const mapDispatchToProps = (dispatch) => ({
  submitAddress: ({formData, isManual}) => dispatch(kycActions.submitAddress({ formData, isManual })),
  fetchPincode: (addressType, pincode) => dispatch(kycActions.fetchPincode(addressType, pincode)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Address);
